import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/components/providers/auth-provider'
import { useTheme } from '@/components/providers/theme-provider'
import {
  publicNavItems,
  authenticatedNavItems,
  hasAccess
} from '@/lib/navigation'
import { config } from '@/lib/config'
import { UserMenu } from './user-menu'
import { SettingsMenu } from './settings-menu'
import { TierIndicator } from './tier-indicator'
import { MobileMenu } from './mobile-menu'
import { QuickExport } from './quick-export'
import { NavLink } from './nav-link'
import { Menu, Sun, Moon } from 'lucide-react'

export function TopNavigation() {
  const { isAuthenticated, user } = useAuth()
  const { setTheme, actualTheme } = useTheme()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  const navItems = isAuthenticated ? authenticatedNavItems : publicNavItems
  const userTier = user?.subscription_tier || 'free'

  const toggleTheme = () => {
    setTheme(actualTheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <>
      <header className="border-b border-border bg-card backdrop-blur supports-[backdrop-filter]:bg-card/95">
        {/* UPDATED: Using bg-card for cohesive look */}
        <div className="container-custom">
          <div className="flex h-16 items-center justify-between">
            {/* Left Side - Logo and Nav */}
            <div className="flex items-center space-x-6">
              {/* Logo */}
              <Link
                to={isAuthenticated ? '/dashboard' : '/'}
                className="flex items-center mr-15 logo-aurora"
              >
                {/* Full logo */}
                <img
                  src="/images/ICON_WHITE.svg"
                  alt="Digestr Logo"
                  className="h-8 w-64 hidden lg:block -ml-20"
                />
                {/* Icon only */}
                <img
                  src="/images/ICON_WHITE.svg"
                  alt="Digestr Logo"
                  className="h-8 w-20 lg:hidden mr-10"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center space-x-1 ">
                {navItems
                  .filter(item => isAuthenticated ? hasAccess(item, userTier) : true)
                  .map((item) => (
                    <NavLink
                      key={item.key}
                      href={item.href}
                      icon={item.icon}
                      isActive={location.pathname === item.href}
                      locked={isAuthenticated && item.requiredTier && !hasAccess(item, userTier)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
              </nav>
            </div>

            {/* Right Side - User Actions */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle - UPDATED: Better styling */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Toggle theme"
              >
                {actualTheme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {isAuthenticated ? (
                <>
                  {/* Settings Menu */}
                  <div className="hidden md:block">
                    <SettingsMenu />
                  </div>

                  {/* Tier Indicator */}
                  <div className="hidden md:block">
                    <TierIndicator tier={userTier} />
                  </div>

                  {/* User Menu */}
                  <div className="hidden md:block">
                    <UserMenu user={user} />
                  </div>

                  {/* Quick Export */}
                  <div className="hidden md:block">
                    <QuickExport />
                  </div>
                </>
              ) : (
                /* Public Auth Buttons - UPDATED: Purple gradient */
                <div className="hidden md:flex items-center space-x-3">
                  <Link
                    to="/auth/login"
                    className="text-sm font-medium text-muted-foreground hover:text-digestr-500 transition-colors"
                  >
                    Sign in
                  </Link>
                  {/* UPDATED: Using btn-gradient class */}
                  <Link
                    to="/auth/register"
                    className="btn-gradient px-4 py-2 text-sm font-medium"
                  >
                    Get Started
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isAuthenticated={isAuthenticated}
        userTier={userTier}
        user={user}
      />
    </>
  )
}